const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class ReviewsSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-reviews-viewport]');
    this.track = this.querySelector('[data-reviews-track]');
    this.previousButton = this.querySelector('[data-reviews-previous]');
    this.nextButton = this.querySelector('[data-reviews-next]');

    if (!this.viewport || !this.track || !this.previousButton || !this.nextButton) return;

    this.loop = this.dataset.loop === 'true';
    this.autoplay = this.dataset.autoplay === 'true' && !REDUCED_MOTION.matches;
    this.autoplaySpeed = Math.max(Number(this.dataset.autoplaySpeed) || 4, 2) * 1000;
    this.isPointerOver = false;
    this.isFocusWithin = false;
    this.isEditorPaused = false;

    this.previousButton.addEventListener('click', this.handlePrevious);
    this.nextButton.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    this.viewport.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);

    requestAnimationFrame(() => this.rebuild());
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this.scrollEndTimer);
    cancelAnimationFrame(this.resizeFrame);
    this.resizeObserver?.disconnect();

    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    this.viewport?.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
  }

  rebuild() {
    if (!this.viewport || !this.track) return;

    const previousBehavior = this.viewport.style.scrollBehavior;
    this.viewport.style.scrollBehavior = 'auto';
    this.track.querySelectorAll('[data-review-clone]').forEach((clone) => clone.remove());

    this.originalCards = Array.from(this.track.querySelectorAll(':scope > [data-review-card]:not([data-review-clone])'));
    this.canScroll = false;
    this.segmentWidth = 0;
    this.segmentStart = 0;

    if (this.originalCards.length > 1 && this.viewport.clientWidth > 0) {
      const firstCard = this.originalCards[0];
      const lastCard = this.originalCards[this.originalCards.length - 1];
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const contentWidth = lastCard.offsetLeft + lastCard.offsetWidth - firstCard.offsetLeft;

      this.canScroll = contentWidth > this.viewport.clientWidth + 1;
      this.segmentWidth = contentWidth + gap;

      if (this.canScroll && this.loop && this.segmentWidth > 0) {
        const copiesPerSide = Math.ceil(this.viewport.clientWidth / this.segmentWidth) + 1;
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();

        for (let copy = 0; copy < copiesPerSide; copy += 1) {
          this.originalCards.forEach((card) => before.append(this.createClone(card)));
          this.originalCards.forEach((card) => after.append(this.createClone(card)));
        }

        this.track.insertBefore(before, firstCard);
        this.track.append(after);
        this.segmentStart = firstCard.offsetLeft;
        this.viewport.scrollLeft = this.segmentStart;
      } else {
        this.viewport.scrollLeft = 0;
      }
    } else {
      this.viewport.scrollLeft = 0;
    }

    this.viewport.style.scrollBehavior = previousBehavior;
    this.updateControls();
    this.restartAutoplay();
  }

  createClone(card) {
    const clone = card.cloneNode(true);
    clone.setAttribute('data-review-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('data-shopify-editor-block');
    clone.removeAttribute('data-review-block-id');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) =>
      element.removeAttribute('data-shopify-editor-block')
    );
    return clone;
  }

  move(direction) {
    if (!this.canScroll || !this.originalCards.length) return;

    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = this.originalCards[0].offsetWidth + gap;
    this.viewport.scrollBy({ left: distance * direction, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    this.restartAutoplay();
  }

  normalizePosition() {
    if (!this.canScroll || !this.loop || !this.segmentWidth) return;

    const lowerBound = this.segmentStart - this.segmentWidth;
    const upperBound = this.segmentStart + this.segmentWidth;
    let nextPosition = this.viewport.scrollLeft;

    while (nextPosition < lowerBound) nextPosition += this.segmentWidth;
    while (nextPosition >= upperBound) nextPosition -= this.segmentWidth;

    if (Math.abs(nextPosition - this.viewport.scrollLeft) > 1) {
      const previousBehavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = nextPosition;
      this.viewport.style.scrollBehavior = previousBehavior;
    }
  }

  updateControls() {
    if (!this.previousButton || !this.nextButton || !this.viewport) return;

    if (!this.canScroll) {
      this.previousButton.hidden = true;
      this.nextButton.hidden = true;
      return;
    }

    if (this.loop) {
      this.previousButton.hidden = false;
      this.nextButton.hidden = false;
      return;
    }

    this.previousButton.hidden = this.viewport.scrollLeft <= 1;
    this.nextButton.hidden =
      this.viewport.scrollLeft + this.viewport.clientWidth >= this.viewport.scrollWidth - 1;
  }

  startAutoplay() {
    if (
      !this.autoplay ||
      !this.canScroll ||
      this.autoplayTimer ||
      this.isPointerOver ||
      this.isFocusWithin ||
      this.isEditorPaused ||
      document.hidden
    ) {
      return;
    }

    this.autoplayTimer = window.setInterval(() => this.move(1), this.autoplaySpeed);
  }

  stopAutoplay() {
    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = undefined;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  handlePrevious = () => this.move(-1);

  handleNext = () => this.move(1);

  handleScroll = () => {
    this.updateControls();
    clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = window.setTimeout(() => this.normalizePosition(), 120);
  };

  handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.move(-1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.move(1);
    }
  };

  handleMouseEnter = () => {
    this.isPointerOver = true;
    this.stopAutoplay();
  };

  handleMouseLeave = () => {
    this.isPointerOver = false;
    this.startAutoplay();
  };

  handleFocusIn = () => {
    this.isFocusWithin = true;
    this.stopAutoplay();
  };

  handleFocusOut = () => {
    requestAnimationFrame(() => {
      this.isFocusWithin = this.contains(document.activeElement);
      this.startAutoplay();
    });
  };

  handleVisibilityChange = () => {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  handleResize = () => {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => this.rebuild());
  };

  handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const selectedCard = event.target.closest('[data-review-card]:not([data-review-clone])');
    if (!selectedCard) return;

    this.isEditorPaused = true;
    this.stopAutoplay();
    this.viewport.scrollTo({
      left: selectedCard.offsetLeft,
      behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
    });
  };

  handleBlockDeselect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.isEditorPaused = false;
    this.startAutoplay();
  };
}

if (!customElements.get('reviews-slider')) {
  customElements.define('reviews-slider', ReviewsSlider);
}
